/**
 * location-edit.js
 */

import React from 'react';
import { Typography, IconButton, ListItemAvatar, ListItemText } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { getCurrentAddress } from 'utils/location';
import { UserContext } from 'contexts/user/user-context';
import { AppMessagingContext } from 'contexts/app-messaging/app-messaging-context';
import Icon from 'components/icon';
import FeatureBox from 'components/feature-box';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import Autocomplete from 'components/form-inputs/autocomplete';
import TextField from 'components/form-inputs/text-field';
import Select from 'components/form-inputs/select';


const useStyles = makeStyles(theme => ({
  locationHelpText: {
    paddingTop: 0,
    paddingBottom: 0,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  locationDetail: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    '& .MuiTextField-root': {
      marginTop: 0
    }
  },
  locationPreview: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.sys.onSurfaceVariant,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      marginTop: theme.spacing(-1),
    },
  },
  locationsPopper: {
    width: 'auto !important',
    maxWidth: 300,
  },
  locationOption: {
    paddingLeft: theme.spacing(1.5),
    '& > *': {
      display: 'inherit',
      alignItems: 'inherit',
      overflow: 'hidden',
    },
    '& .MuiListItemAvatar-root': {
      minWidth: 'auto',
      marginRight: theme.spacing(1),
    },
    '& .MuiListItemText-primary': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .MuiListItemText-secondary': {
      fontSize: theme.typography.sizes[0],      
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .add-new': {
      color: theme.palette.primary.main,
      '& .MuiListItemText-primary': {
        fontWeight: 'bold'
      }
    }
  },
}));


// location autocomplete filter options
const locationFilterOptions = createFilterOptions({
  limit: 20,
  stringify: option => `${option.locationName} ${option.address1} ${option.city}`,
});


const LocationOption = (props) => {
  const { liProps, option } = props;
  const { t } = useTranslation('location');

  return (
    <li {...liProps} key={option.id}>
      <div className={`${option.makeNewLocation ? 'add-new' : ''}`}>
        <ListItemAvatar>
          <Icon type={option.makeNewLocation ? 'location-add' : 'location'} fontSize='large' />
        </ListItemAvatar>
        <ListItemText
          primary={option.makeNewLocation 
            ? `${t('location.locationName.newLocation')}${option.inputValue ? `: "${option.inputValue}"` : ''}` 
            : option.locationName}
          secondary={option.makeNewLocation ? '' : <>{option.address1} {option.city}</>}
        />
      </div>
    </li>
  );
}


export function LocationEditFields(props) {
  const {
    focusHandlers,
    locationOptions,
    countryOptions,
    presetOptions,
    recordType,
    doAutofill,
    weatherPreview,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation('location');
  const { user } = React.useContext(UserContext);
  const { getLocation } = React.useContext(AppMessagingContext);
  const { enqueueSnackbar } = useSnackbar();
  const [isLocationLoading, setIsLocationLoading] = React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState();
  const [triggerOpenAccordion, setTriggerOpenAccordion] = React.useState(
    (!doAutofill && (locationOptions.length === 0 || recordType === Types.Record.DEPLOYMENT)) ? 1 : 0
  );  
  const [isSectionOpen, setIsSectionOpen] = React.useState(triggerOpenAccordion > 0);
  const [didAutofill, setDidAutofill] = React.useState(false);
  const [focusAddress1, setFocusAddress1] = React.useState(0);
  const cancelLocation = React.useRef(false);

  // hook form
  const formContext = useFormContext();
  const { control, formState, setValue, resetField, clearErrors } = formContext;
  const locationNameValue = useWatch({ control, name: 'location.locationName' });
  const address1Value = useWatch({ control, name: 'location.address1' });
  const cityValue = useWatch({ control, name: 'location.city' });
  const countryValue = useWatch({ control, name: 'location.country' });
  const regionValue = useWatch({ control, name: 'location.region' });
  const locationErrors = formState.errors?.location;
  const setValProps = React.useMemo(() => ({
    shouldDirty: true,
  }), []);


  // address setter
  const setAddress = React.useCallback((
    {
      address1,
      address2,
      city,
      country,
      postCode,
      region,
    },
    ignoreCommonComponents = false,
    reset = false,
  ) => {

    const setAddressField = (name, value) => {
      const valueOrBlank = value || '';
      if (reset) {
        resetField(name, { defaultValue: valueOrBlank });
      } else {
        setValue(name, valueOrBlank, setValProps);
      }
    };

    setAddressField('location.address1', address1);
    setAddressField('location.address2', address2);
    setAddressField('location.postCode', postCode);

    // option to leave these alone so they aren't entering same stuff too often
    if (!ignoreCommonComponents) {
      setAddressField('location.city', city);
      setAddressField('location.country', country);
      // region is in a timeout to let the country change update the region options
      // before setting the region value. otherwise we get a warning.
      setTimeout(() => {
        setAddressField('location.region', region);
      });
    }
    
  }, [setValue, setValProps, resetField]);


  // if mobile and creating, use current location
  React.useEffect(() => {
    // autofill city/region/country if possible
    if (!countryValue && presetOptions?.country?.code) {
      setValue('location.country', presetOptions.country.code);
      setValue('location.city', presetOptions.city);
      setTimeout(() => {
        setValue('location.region', presetOptions.region.code);
      });
    }

    if (user && doAutofill) {
      setIsLocationLoading(true);
      (async() => {
        try {
          const address = await getCurrentAddress(user.apiKeys.Google, getLocation);

          if (cancelLocation.current) {
            return;
          }

          setAddress({
            ...address,
            address1: `${address.streetNumber} ${address.street}`.trim()
          }, false, true);
          resetField('location.locationName', { defaultValue: `${address.streetNumber} ${address.street}`.trim() });
          resetField('location.latitude', { defaultValue: address.latitude });
          resetField('location.longitude', { defaultValue: address.longitude });
          setDidAutofill(true);
        } catch (e) {
          enqueueSnackbar('Unable to determine your current location', {
            variant: 'error'
          });
        } finally {
          setIsLocationLoading(false);
        }
      })();
    }
  // including countryValue causes iOS devices to error with "undefined is not a function
  // (near ...setCoordinates...)" on line 103 of location.js. TODO: find a better solution.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, doAutofill, presetOptions, getLocation, enqueueSnackbar, setValue, setAddress]);


  // setup country & region options when country or region changes
  React.useEffect(() => {
    if (countryOptions && countryValue) {
      const country = countryOptions.find(c => c.code === countryValue);
      if (country) {
        if (!regionValue || !country.regions.find(r => r.code === regionValue)) {
          setValue('location.region', country.regions[0].code);
        }
        setSelectedCountry(country);
      }
    }
  }, [countryOptions, countryValue, regionValue, setValue]);


  // if there are any location errors, open the accordion
  React.useEffect(() => {
    if (Boolean(locationErrors)) {
      setTriggerOpenAccordion(p => p + 1);
    }
  }, [locationErrors]);


  const getOptionLabel = (option) => {
    return typeof option === 'string'
      ? option
      : option.inputValue
        ? option.inputValue
        : option.locationName;
  }


  const filterOptions = (options, params) => {
    const filtered = locationFilterOptions(options, params);

    // always show 'ADD NEW LOCATION' option
    return [
      {
        makeNewLocation: true,
        inputValue: params.inputValue,
        locationName: params.inputValue
      },
      ...filtered
    ];
  }


  const onChange = (e, location) => {
    location = location || {};

    if (location.makeNewLocation) {
      setAddress({}, true);
      setValue('location.latitude', '');
      setValue('location.longitude', '');
      setValue('location.locationName', location.inputValue || '', setValProps);
      setTriggerOpenAccordion(p => p + 1);

      // if user has typed a location name and clicked 'make new location' then change focus to address1
      if (location.locationName) {
        setFocusAddress1(p => p + 1);
      }
    }
    // update address fields (location.locationName is already updated)
    else if (location.address1 || location.inputValue) {
      setAddress(location);
    }

    clearErrors('location');
  }


  const onInputChange = (val) => {
    // on input change, if no suggestions then expand and clear for new address
    const filtered = locationFilterOptions(locationOptions, {
      inputValue: val,
      getOptionLabel
    });

    // when user types a location with no filtered results and it wasn't auto-filled
    // we assume they are creating a new location.
    // clear only the fields that are likely to be used and open the editor.
    if (filtered.length === 0 && !isSectionOpen && !didAutofill) {
      setTriggerOpenAccordion(p => p + 1);
      setAddress({}, true);
      setValue('location.latitude', '');
      setValue('location.longitude', '');
    }
  }


  return (
    <FeatureBox
      loading={isLocationLoading}
      message={<>
        <span>Getting current location.</span>
        <IconButton
          color='primary'
          style={{ top: -2 }}
          onClick={() => {
            cancelLocation.current = true;
            setIsLocationLoading(false);
          }}
          size="large">
          <Icon type='close' />
        </IconButton>
      </>}
      loadingDirection='row'
      spinnerSize={30}
    >

      <EditorAccordion
        shiftButton
        name={t('location.sectionName')}
        data-testid='location-accordion'
        expand={triggerOpenAccordion}
        onChange={v => setIsSectionOpen(v)}
        summary={<>
          <FormGridContainer>
            <FormGridItem md={7}>

              {/* prevent autocomplete interaction from opening accordion */}
              <div onClick={e => e.stopPropagation()}>
                <Autocomplete
                  name='location.locationName'
                  label={t('location.locationName.label')}
                  classes={{ option: classes.locationOption, popper: classes.locationsPopper }}
                  options={locationOptions || []}
                  focusHandlers={focusHandlers}
                  makeValue={locationName => ({ locationName })}
                  setValueAs={val => val ? val.locationName : ''}
                  rules={{
                    required: recordType === Types.Record.EVENT ? t('location.locationName.errMsgEmpty') : undefined,
                    validate: value => recordType === Types.Record.EVENT ? value.trim() !== '' || t('location.locationName.errMsgEmpty') : true,
                    maxLength: {
                      value: 150,
                      message: t('location.locationName.errMsgLength')
                    }
                  }}
                  getOptionLabel={getOptionLabel}
                  filterOptions={filterOptions}
                  renderOption={(props, option) => <LocationOption liProps={props} option={option} />}
                  onChange={onChange}
                  onInputChange={onInputChange}
                />
              </div>

            </FormGridItem>
            <FormGridItem md={5} style={{ display: 'flex', alignItems: 'center' }}>
              <div className={classes.locationPreview}>
                <Typography variant='caption'>
                  <div>{address1Value} {cityValue ? `${cityValue},` : ''} {regionValue}</div>
                  {weatherPreview &&
                    <div>{weatherPreview}</div>
                  }
                </Typography>
              </div>
            </FormGridItem>
          </FormGridContainer>
        </>}

        detail={
          <div className={classes.locationDetail}>

            {/* address lines */}
            <FormGridContainer direction='column'>
              <FormGridItem md={7}>
                <TextField
                  name='location.address1'
                  label={t('location.address1.label')}
                  focusHandlers={focusHandlers}
                  doFocus={focusAddress1}
                  onBlur={(e) => {
                    if (!locationNameValue) {
                      setValue('location.locationName', e.target.value);
                    }
                  }}
                  rules={{
                    required: t('location.address1.errMsgEmpty'),
                    validate: value => value.trim() !== '' || t('location.address1.errMsgEmpty'),
                    maxLength: {
                      value: 150,
                      message: t('location.address1.errMsgLength')
                    }
                  }}
                />
              </FormGridItem>
              <FormGridItem md={7}>
                <TextField
                  name='location.address2'
                  label={t('location.address2.label')}
                  focusHandlers={focusHandlers}
                  rules={{
                    maxLength: {
                      value: 150,
                      message: t('location.address1.errMsgLength')
                    }
                  }}
                />
              </FormGridItem>

              {/* location city */}
              <FormGridItem md={7}>
                <TextField
                  name='location.city'
                  label={t('location.city.label')}
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('location.city.errMsgEmpty'),
                    validate: value => value.trim() !== '' || t('location.city.errMsgEmpty'),
                    maxLength: {
                      value: 150,
                      message: t('location.city.errMsgLength')
                    }
                  }}
                />
              </FormGridItem>

              {/* location state */}
              {selectedCountry &&
                <FormGridItem md={7}>
                  <Select
                    name='location.region'
                    label={selectedCountry.regionTerm}
                    focusHandlers={focusHandlers}
                    rules={{
                      required: `${t('location.region.errMsgEmptyPart')} ${selectedCountry.regionTerm}`,
                      validate: value => `${value}`.trim() !== '' || `${t('location.region.errMsgEmptyPart')} ${selectedCountry.regionTerm}`,
                    }}
                    options={selectedCountry.regions.map(r => ({ id: r.code, name: r.name }))}
                  />
                </FormGridItem>}

              {/* location country */}
              <FormGridItem md={7}>
                <Select
                  name='location.country'
                  label={t('location.country.label')}
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('location.country.errMsgEmpty'),
                    validate: value => `${value}`.trim() !== '' || t('location.country.errMsgEmpty'),
                  }}
                  options={countryOptions.map(r => ({ id: r.code, name: r.name }))}
                />
              </FormGridItem>

              {/* location post code */}
              {selectedCountry &&
                <FormGridItem md={7}>
                  <TextField
                    name='location.postCode'
                    label={selectedCountry.postCodeTerm}
                    focusHandlers={focusHandlers}
                    rules={{
                      maxLength: {
                        value: 10,
                        message: t('location.postCode.errMsgLength')
                      }
                    }}
                  />
                </FormGridItem>}

            </FormGridContainer>
          </div>}
      />
    </FeatureBox>
  );
}


export default function LocationEdit(props) {
  const { recordType, drawerState } = props;
  const classes = useStyles();
  const { t } = useTranslation('location');


  return (
      <EditorIconSection
        iconName='location'
        helpProps={{
          translation: 'location',
          section: recordType === Types.Record.EVENT ? 'event' : 'deployment'
        }}
      >
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t(`${recordType === Types.Record.EVENT ? 'event' : 'deployment'}.location.helpTitle`)}
          text={t(`${recordType === Types.Record.EVENT ? 'event' : 'deployment'}.location.helpInfo`)}
          className={classes.locationHelpText}
        >
          {focusHandlers =>
            <LocationEditFields {...props} focusHandlers={focusHandlers} />
          }
        </HelpTextItem>
      </EditorIconSection>
  );
}